import type {
  App6EchelonCode,
  App6Entry,
  App6Palette,
  EniOdbKind,
  EniRole,
  OdbKind,
  OdbNode,
  OdbOrganisation,
  OdbSide,
  OdbSymbol,
  OdbVocabulary,
} from "../../api/types";
import { ACTIVE, BY_KEY, ENEMY_KINDS } from "../../echelons";
import { echelonUnitName, entryFor } from "./app6";
import { cleanNumber, nextFreeNumber, renumbered } from "./numbering";
import { app6FromSidc, echelonFromSidc, hqTfFromSidc } from "./sidc";

/** Arbre de l'éditeur d'ordre de bataille : fonctions pures (aucun React). Chaque unité porte son échelon APP-6
 *  et sa fonction APP-6 ; le rang moteur (S/GTIA, GTIA, brigade… ; compagnie, bataillon… côté ennemi) s'en déduit,
 *  jamais de la profondeur. Règles R1 à R4 de la conception (échelons possibles, défauts, cadre force
 *  opérationnelle), vérifiées aussi par le serveur à chaque aperçu. */

/** Chemin d'une unité dans l'arbre de l'éditeur : indices depuis la racine ([] = la racine). */
export type Path = number[];

export const pathKey = (path: Path) => ["0", ...path].join(".");

export function pathFromKey(key: string): Path {
  return key
    .split(".")
    .slice(1)
    .map((part) => Number(part));
}

export function nodeAt(tree: OdbNode, path: Path): OdbNode {
  return path.reduce<OdbNode>((node, i) => node.children[i] as OdbNode, tree);
}

/** Unité à un chemin, ou null si l'arbre n'a pas (ou plus) d'unité à cet endroit. */
export function nodeAtOrNull(tree: OdbNode | null | undefined, path: Path): OdbNode | null {
  let node: OdbNode | undefined | null = tree;
  for (const i of path) {
    node = node?.children[i];
    if (!node) return null;
  }
  return node ?? null;
}

/** Copie de l'arbre où l'unité du chemin est remplacée par `fn(unité)`. */
export function updateAt(tree: OdbNode, path: Path, fn: (node: OdbNode) => OdbNode): OdbNode {
  if (path.length === 0) return fn(tree);
  const [head, ...rest] = path as [number, ...number[]];
  return { ...tree, children: tree.children.map((c, i) => (i === head ? updateAt(c, rest, fn) : c)) };
}

export function addChild(tree: OdbNode, path: Path, child: OdbNode): OdbNode {
  return updateAt(tree, path, (n) => ({ ...n, children: [...n.children, child] }));
}

/** Insère `child` sous l'unité `parent`, au rang `index` (borné). */
export function insertAt(tree: OdbNode, parent: Path, index: number, child: OdbNode): OdbNode {
  return updateAt(tree, parent, (n) => {
    const children = [...n.children];
    children.splice(Math.max(0, Math.min(index, children.length)), 0, child);
    return { ...n, children };
  });
}

export function removeAt(tree: OdbNode, path: Path): OdbNode {
  if (path.length === 0) return tree;
  const parent = path.slice(0, -1);
  const index = path[path.length - 1] as number;
  return updateAt(tree, parent, (n) => ({ ...n, children: n.children.filter((_, i) => i !== index) }));
}

/** Déplace une unité parmi ses sœurs (−1 : vers le haut) ; l'ordre est celui des emplacements de la manœuvre. */
export function moveAt(tree: OdbNode, path: Path, delta: -1 | 1): OdbNode {
  if (path.length === 0) return tree;
  const parent = path.slice(0, -1);
  const index = path[path.length - 1] as number;
  return updateAt(tree, parent, (n) => {
    const target = index + delta;
    if (target < 0 || target >= n.children.length) return n;
    const children = [...n.children];
    [children[index], children[target]] = [children[target] as OdbNode, children[index] as OdbNode];
    return { ...n, children };
  });
}

export function countUnits(node: OdbNode): number {
  return 1 + node.children.reduce((sum, c) => sum + countUnits(c), 0);
}

/** Parcours de l'arbre en profondeur, racine comprise. */
export function walk(tree: OdbNode, visit: (node: OdbNode, path: Path) => void, path: Path = []): void {
  visit(tree, path);
  tree.children.forEach((child, i) => walk(child, visit, [...path, i]));
}

/** Copie profonde sans codes : la copie reçoit de nouveaux codes du serveur (une unité ne se dédouble jamais). */
export function withoutCodes(node: OdbNode): OdbNode {
  return { ...node, code: null, children: node.children.map(withoutCodes) };
}

// --- Échelons, rangs et défauts (R1 à R4) ----------------------------------------------------------------------

export const isEnemyKind = (kind: OdbKind | null | undefined) => Boolean(kind && ENEMY_KINDS.includes(kind));
export const sideOf = (kind: OdbKind | null | undefined): OdbSide => (isEnemyKind(kind) ? "ENI" : "AMI");

/** R2 : échelons possibles pour la racine de chaque nature (le premier est le défaut), d'après la table des échelons
 *  (registre B47) ; un ODB ennemi plus bas que la division admet une racine sans échelon (« 00 »). */
const DIVISION_ORDER = BY_KEY.DIVISION!.order;
export const ROOT_ECHELONS = Object.fromEntries([
  ...ACTIVE.flatMap((e) => (e.odb_kind ? [[e.odb_kind, [...e.app6]]] : [])),
  ...ACTIVE.flatMap((e) => (e.eni_odb_kind ? [[e.eni_odb_kind, e.order >= DIVISION_ORDER ? [...e.app6] : [...e.app6, "00"]]] : [])),
]) as Record<OdbKind, App6EchelonCode[]>;

/** R3 : échelon par défaut d'un nouvel enfant selon celui de son parent (rien sous une équipe). */
export const CHILD_ECHELON: Record<App6EchelonCode, App6EchelonCode | null> = {
  // Au-dessus de la division (corps, une fois en service) : l'échelon par défaut du niveau subordonné.
  ...Object.fromEntries(
    ACTIVE.filter((e) => e.order > DIVISION_ORDER && e.child).flatMap((e) => e.app6.map((code) => [code, BY_KEY[e.child!]!.app6[0]])),
  ),
  "21": "18",
  "18": "16",
  "17": "16",
  "16": "15",
  "15": "14",
  "14": "12",
  "13": "12",
  "12": "11",
  "11": null,
  "00": null,
};

/** Rang moteur d'un échelon : types du vocabulaire (« SGTIA », « GTIA »… ; « Compagnie », « Bataillon »… pour
 *  l'ennemi). */
const RANK_BELOW_COMPANY: Record<string, string> = { "11": "Equipe", "12": "Groupe", "13": "Section", "14": "Section" };
const RANK_AMI: Record<string, string> = {
  ...RANK_BELOW_COMPANY,
  ...Object.fromEntries(ACTIVE.flatMap((e) => e.app6.map((code) => [code, e.rank]))),
};
const RANK_ENI: Record<string, string> = {
  ...RANK_BELOW_COMPANY,
  ...Object.fromEntries(ACTIVE.flatMap((e) => (e.eni_rank ? e.app6.map((code) => [code, e.eni_rank as string]) : []))),
};
/** Rang enfant côté ennemi (unité sans échelon : elle prend le rang sous celui de son parent). */
const CHILD_RANK_ENI: Record<string, string | null> = {
  Division: "Brigade",
  Brigade: "Bataillon",
  Bataillon: "Compagnie",
  Compagnie: "Section",
  Section: "Groupe",
  Groupe: "Equipe",
  Equipe: null,
};
const RANK_ECHELON_ENI: Record<string, App6EchelonCode> = {
  Division: "21",
  Brigade: "18",
  Bataillon: "16",
  Compagnie: "15",
  Section: "14",
  Groupe: "12",
  Equipe: "11",
};
const KIND_ROOT_RANK = Object.fromEntries([
  ...ACTIVE.flatMap((e) => (e.odb_kind ? [[e.odb_kind, e.rank]] : [])),
  ...ACTIVE.flatMap((e) => (e.eni_odb_kind ? [[e.eni_odb_kind, e.eni_rank ?? ""]] : [])),
]) as Record<OdbKind, string>;

export interface TreeContext {
  kind: OdbKind;
  palette: App6Palette;
  vocabulary: OdbVocabulary;
}

export function rootEchelons(ctx: TreeContext): App6EchelonCode[] {
  return ctx.vocabulary.root_echelons?.[ctx.kind] ?? ROOT_ECHELONS[ctx.kind];
}

function childDefault(ctx: TreeContext, parent: App6EchelonCode): App6EchelonCode | null {
  const map = ctx.vocabulary.child_echelon;
  if (map && parent in map) return map[parent] ?? null;
  return CHILD_ECHELON[parent];
}

/** Échelons et rangs effectifs de chaque unité du chemin, de la racine à l'unité. */
function chain(tree: OdbNode, path: Path, ctx: TreeContext): { echelon: App6EchelonCode; rank: string | null }[] {
  const enemy = isEnemyKind(ctx.kind);
  const out: { echelon: App6EchelonCode; rank: string | null }[] = [];
  let node = tree;
  for (let depth = 0; depth <= path.length; depth += 1) {
    if (depth > 0) node = node.children[path[depth - 1] as number] as OdbNode;
    const previous = out[depth - 1];
    let echelon: App6EchelonCode;
    if (node.echelon) echelon = node.echelon;
    else if (!previous) echelon = rootEchelons(ctx)[0] ?? "16";
    else if (previous.echelon === "00") echelon = (previous.rank && RANK_ECHELON_ENI[CHILD_RANK_ENI[previous.rank] ?? ""]) || "14";
    else echelon = childDefault(ctx, previous.echelon) ?? "11";
    let rank: string | null;
    if (echelon === "00") rank = previous ? (previous.rank ? (CHILD_RANK_ENI[previous.rank] ?? null) : null) : KIND_ROOT_RANK[ctx.kind];
    else rank = (enemy ? RANK_ENI : RANK_AMI)[echelon] ?? null;
    out.push({ echelon, rank });
  }
  return out;
}

/** Échelon effectif d'une unité (le sien, sinon le défaut de R2 / R3). */
export function echelonAt(tree: OdbNode, path: Path, ctx: TreeContext): App6EchelonCode {
  return chain(tree, path, ctx)[path.length]?.echelon ?? "16";
}

/** Rang moteur d'une unité (type du vocabulaire), déduit de son échelon. */
export function rankAt(tree: OdbNode, path: Path, ctx: TreeContext): string | null {
  return chain(tree, path, ctx)[path.length]?.rank ?? null;
}

/** Rang placé par la manœuvre sous la racine de chaque nature amie (S/GTIA, GTIA, brigades). */
export const PLACED_RANK: Partial<Record<OdbKind, string>> = Object.fromEntries(
  ACTIVE.flatMap((e) => (e.odb_kind && e.child ? [[e.odb_kind, BY_KEY[e.child]!.rank]] : [])),
);

/** R4 : cadre force opérationnelle par défaut — S/GTIA et GTIA d'une organisation en groupements tactiques
 *  (ami), jamais côté ennemi ni en unités organiques. */
export function defaultTf(ctx: TreeContext, organisation: OdbOrganisation | undefined, echelon: App6EchelonCode): boolean {
  return !isEnemyKind(ctx.kind) && organisation !== "organique" && (echelon === "15" || echelon === "16");
}

export function tfAt(tree: OdbNode, path: Path, ctx: TreeContext): boolean {
  const node = nodeAt(tree, path);
  return node.tf ?? defaultTf(ctx, tree.organisation, echelonAt(tree, path, ctx));
}

/** Une équipe n'a pas de subordonnés. */
export function canHaveChildren(echelon: App6EchelonCode): boolean {
  return echelon !== "11";
}

/** Pourquoi un échelon est refusé à l'unité du chemin (R1, R2), ou null s'il est possible. */
export type EchelonRefusal = "root" | "parent" | "children" | "side" | null;

export function echelonRefusal(tree: OdbNode, path: Path, ctx: TreeContext, code: App6EchelonCode): EchelonRefusal {
  const enemy = isEnemyKind(ctx.kind);
  if (code === "00" && !enemy) return "side";
  if (path.length === 0) {
    if (!rootEchelons(ctx).includes(code)) return "root";
  } else if (code !== "00") {
    // Sous une unité sans échelon (« 00 »), comparaison au rang qu'elle tient pour le moteur (comme le serveur).
    const parent = chain(tree, path.slice(0, -1), ctx)[path.length - 1];
    const bound = parent?.echelon === "00" ? (parent.rank ? RANK_ECHELON_ENI[parent.rank] : undefined) : parent?.echelon;
    if (bound && Number(code) >= Number(bound)) return "parent";
  }
  if (code === "00") return null;
  const node = nodeAt(tree, path);
  const blocked = node.children.some((_, i) => {
    const child = echelonAt(tree, [...path, i], ctx);
    return child !== "00" && Number(child) >= Number(code);
  });
  return blocked ? "children" : null;
}

/** Quantité de matériels par défaut d'un rang (vocabulaire du serveur ; 1 pour une équipe). */
export function defaultQuantity(ctx: TreeContext, rank: string | null): number | null {
  if (!rank) return null;
  return ctx.vocabulary.types.find((t) => t.key === rank)?.quantity ?? (rank === "Equipe" ? 1 : null);
}

/** Échelon d'un nouvel enfant de l'unité `parent` (R3), ou null si elle n'en a pas (équipe). */
export function childEchelonAt(tree: OdbNode, parent: Path, ctx: TreeContext): App6EchelonCode | null {
  const links = chain(tree, parent, ctx);
  const own = links[parent.length];
  if (!own) return null;
  if (own.echelon === "00") {
    const rank = own.rank ? CHILD_RANK_ENI[own.rank] : null;
    return rank ? (RANK_ECHELON_ENI[rank] ?? null) : null;
  }
  return childDefault(ctx, own.echelon);
}

/** Nouvelle unité sous `parent` : échelon de R3, fonction de son parent, T suivant libre parmi ses sœurs de même
 *  échelon, M automatique, nom « {Échelon} {T} » dans la langue de l'interface, matériels de sa formation et nombre
 *  par défaut de son rang. Null si le parent n'a pas de subordonnés (équipe). */
export function newUnit(tree: OdbNode, parent: Path, ctx: TreeContext, siblingsAfter: OdbNode[] = []): OdbNode | null {
  const echelon = childEchelonAt(tree, parent, ctx);
  if (!echelon) return null;
  const owner = nodeAt(tree, parent);
  const number = nextFreeNumber([...owner.children, ...siblingsAfter], echelon);
  const rank = isEnemyKind(ctx.kind) ? RANK_ENI[echelon] : RANK_AMI[echelon];
  return {
    code: null,
    name: echelonUnitName(ctx.palette, echelon, number),
    echelon,
    fonction: owner.fonction ?? null,
    app6: owner.app6 ?? null,
    hq: false,
    tf: null,
    manoeuvre: null,
    number,
    higher: null,
    specialty: owner.specialty ?? null,
    materiel: [...owner.materiel],
    quantity: defaultQuantity(ctx, rank ?? null),
    ...(isEnemyKind(ctx.kind) ? { role: null } : {}),
    children: [],
  };
}

/** Ajoute `count` unités sous `parent` (scission ×1 à ×4), T enchaînés. */
export function addUnits(tree: OdbNode, parent: Path, ctx: TreeContext, count = 1): OdbNode {
  const added: OdbNode[] = [];
  for (let i = 0; i < count; i += 1) {
    const unit = newUnit(tree, parent, ctx, added);
    if (!unit) break;
    added.push(unit);
  }
  return updateAt(tree, parent, (n) => ({ ...n, children: [...n.children, ...added] }));
}

/** Copie profonde de l'unité juste après l'original, codes vidés, T suivant libre parmi ses sœurs. Un nom par défaut
 *  (« Compagnie 3 ») suit le nouveau T (« Compagnie 4 ») ; un nom saisi est gardé. */
export function duplicateAt(tree: OdbNode, path: Path, ctx?: TreeContext): { tree: OdbNode; path: Path } {
  if (path.length === 0) return { tree, path };
  const parent = path.slice(0, -1);
  const index = path[path.length - 1] as number;
  const original = nodeAt(tree, path);
  const siblings = nodeAt(tree, parent).children;
  const number = nextFreeNumber(siblings, original.echelon);
  let name = original.name;
  if (ctx) {
    const echelon = echelonAt(tree, path, ctx);
    if (original.name.trim() === echelonUnitName(ctx.palette, echelon, cleanNumber(original.number))) name = echelonUnitName(ctx.palette, echelon, number);
  }
  const copy = { ...withoutCodes(original), name, number };
  return { tree: insertAt(tree, parent, index + 1, copy), path: [...parent, index + 1] };
}

/** Unité déplacée sous une autre formation (Alt+→, dépôt « dans », collage) : son échelon, s'il n'est plus strictement
 *  inférieur à celui de sa formation (R1), est ramené à l'échelon par défaut sous elle (R3), et de même pour ses
 *  subordonnés qui ne tiendraient plus dessous. Rend l'arbre et l'échelon ramené de l'unité (null si rien ne change). */
export function fitEchelonAt(tree: OdbNode, path: Path, ctx: TreeContext): { tree: OdbNode; lowered: App6EchelonCode | null } {
  let out = tree;
  let lowered: App6EchelonCode | null = null;
  const fit = (at: Path) => {
    const node = nodeAt(out, at);
    if (at.length > 0 && node.echelon && echelonRefusal(out, at, ctx, node.echelon) === "parent") {
      const next = childEchelonAt(out, at.slice(0, -1), ctx);
      if (next) {
        out = updateAt(out, at, (n) => ({ ...n, echelon: next }));
        if (at.length === path.length) lowered = next;
      }
    }
    nodeAt(out, at).children.forEach((_, i) => fit([...at, i]));
  };
  fit(path);
  return { tree: out, lowered };
}

/** Subordonnés de l'unité renumérotés 1, 2, 3… (échelon par échelon). */
export function renumberAt(tree: OdbNode, path: Path): OdbNode {
  return updateAt(tree, path, (n) => ({ ...n, children: renumbered(n.children) }));
}

/** Alt+→ : l'unité devient le dernier subordonné de sa sœur précédente. */
export function indentAt(tree: OdbNode, path: Path): { tree: OdbNode; path: Path } | null {
  const index = path[path.length - 1];
  if (path.length === 0 || !index) return null;
  const parent = path.slice(0, -1);
  const node = nodeAt(tree, path);
  const removed = removeAt(tree, path);
  const target = [...parent, index - 1];
  const size = nodeAt(removed, target).children.length;
  return { tree: addChild(removed, target, node), path: [...target, size] };
}

/** Alt+← : l'unité remonte d'un rang, juste après son ancienne formation. */
export function outdentAt(tree: OdbNode, path: Path): { tree: OdbNode; path: Path } | null {
  if (path.length < 2) return null;
  const parent = path.slice(0, -1);
  const grand = parent.slice(0, -1);
  const node = nodeAt(tree, path);
  const removed = removeAt(tree, path);
  const at = (parent[parent.length - 1] as number) + 1;
  return { tree: insertAt(removed, grand, at, node), path: [...grand, at] };
}

// --- Départ d'un ordre de bataille ami ----------------------------------------------------------------------------

export interface AmiStarter {
  name: string;
  number: string | null;
  higher: string | null;
  fonction: string | null;
  app6: string | null;
  organisation: OdbOrganisation;
  nation: string | null;
  /** « structure » : ce que la manœuvre place ; « racine » : la seule racine. */
  start: "structure" | "racine";
}

/** Arbre de départ d'un ordre de bataille ami : la racine (échelon par défaut de la nature, fonction, numéros), et
 *  pour une structure type ce que la manœuvre de son niveau place — trois compagnies pour un bataillon, quatre
 *  bataillons de trois compagnies pour une brigade, quatre brigades de quatre bataillons pour une division (chaque
 *  brigade sert aussi au niveau brigade, où la manœuvre place quatre GTIA) — avec la fonction de la racine, sans
 *  matériel. */
export function amiStarterTree(ctx: TreeContext, starter: AmiStarter): OdbNode {
  const echelon = rootEchelons(ctx)[0] ?? "16";
  const entry = entryFor(ctx.palette, starter.fonction, starter.app6);
  let tree: OdbNode = {
    code: null,
    name: starter.name,
    echelon,
    fonction: starter.fonction,
    app6: starter.app6,
    hq: false,
    tf: null,
    manoeuvre: null,
    number: starter.number,
    higher: starter.higher,
    specialty: entry?.specialty ?? null,
    materiel: [],
    quantity: defaultQuantity(ctx, KIND_ROOT_RANK[ctx.kind]),
    organisation: starter.organisation,
    nation: starter.nation,
    children: [],
  };
  if (starter.start === "racine") return tree;
  const counts = ctx.kind === "gtia" ? [3] : ctx.kind === "brigade" ? [4, 3] : [4, 4, 3];
  const fill = (path: Path, depth: number) => {
    const count = counts[depth];
    if (!count) return;
    tree = addUnits(tree, path, ctx, count);
    nodeAt(tree, path).children.forEach((_, i) => fill([...path, i], depth + 1));
  };
  fill([], 0);
  return tree;
}

// --- Arbre d'un serveur antérieur ---------------------------------------------------------------------------------

/** Complète un arbre lu (document antérieur à la numérotation, ou serveur qui ne rend pas encore les champs APP-6)
 *  avec l'échelon, la fonction, le QG et la force opérationnelle de son symbole calculé par le serveur ; rien
 *  n'est changé pour une unité qui les porte déjà. */
export function hydrate(tree: OdbNode, symbols: readonly OdbSymbol[], ctx: TreeContext): OdbNode {
  const byPath = new Map(symbols.map((s) => [s.path, s]));
  const fill = (node: OdbNode, path: Path): OdbNode => {
    const symbol = byPath.get(pathKey(path));
    const sidc = symbol?.sidc ?? null;
    const app6 = node.app6 ?? symbol?.app6 ?? app6FromSidc(sidc);
    const entry = node.fonction ? null : entryFor(ctx.palette, symbol?.fonction ?? null, app6);
    const flags = hqTfFromSidc(sidc);
    const echelon =
      node.echelon ?? symbol?.echelon_app6 ?? (sidc && sidc.slice(8, 10) === "00" && isEnemyKind(ctx.kind) ? "00" : echelonFromSidc(sidc));
    return {
      ...node,
      ...(echelon ? { echelon } : {}),
      app6: app6 ?? null,
      fonction: node.fonction ?? symbol?.fonction ?? entry?.key ?? null,
      hq: node.hq ?? symbol?.hq ?? flags.hq,
      tf: node.tf ?? symbol?.tf ?? (sidc ? flags.tf : null),
      children: node.children.map((child, i) => fill(child, [...path, i])),
    };
  };
  return fill(tree, []);
}

/** Type d'unité à une profondeur donnée (ancien éditeur : le rang se déduisait de la profondeur). Gardé pour les
 *  ordres de bataille d'un serveur antérieur sans échelon. */
export function typeAt(vocabulary: OdbVocabulary, kind: OdbKind, depth: number): string | null {
  let type: string | null = vocabulary.kinds.find((k) => k.key === kind)?.root ?? null;
  for (let d = 0; d < depth && type; d += 1) type = vocabulary.types.find((t) => t.key === type)?.child ?? null;
  return type;
}

// --- Départ d'un ordre de bataille ennemi ---------------------------------------------------------------------

export interface EniStarter {
  name: string;
  number: string | null;
  higher: string | null;
  fonction: string | null;
  app6: string | null;
  /** « structure » : structure ternaire générique ; « racine » : la seule racine. */
  start: "structure" | "racine";
}

/** Élément d'une structure ternaire générique : rôle dans la manœuvre, fonction propre (sinon celle de manœuvre),
 *  échelon propre (sinon le défaut sous la racine) et nombre de subordonnés (même rôle, même fonction). */
interface EniPart {
  role: EniRole;
  fonction: string | null;
  echelon: App6EchelonCode | null;
  children: number;
}

const part = (role: EniRole, children: number, fonction: string | null = null, echelon: App6EchelonCode | null = null): EniPart => ({
  role,
  fonction,
  echelon,
  children,
});
/** Deux formations en premier échelon, une en second, de trois subordonnés chacune. */
const MANOEUVRE: EniPart[] = [part("ech1", 3), part("ech1", 3), part("ech2", 3)];

/** Structure ternaire générique de chaque nature ennemie (modèle d'organisation public, sans rapport avec un ennemi
 *  de référence) : reconnaissance, manœuvre ternaire, appuis — de quoi tenir chaque emplacement de la manœuvre que
 *  l'ordre de bataille affronte. Même structure côté serveur pour ses essais (server/tests/odb_otan.py). */
export const ENI_TERNARY: Record<EniOdbKind, EniPart[]> = {
  // Face au régiment : compagnie de reconnaissance de trois sections (les trois emplacements de reconnaissance),
  // trois compagnies de trois sections, compagnie d'appui, section de drones.
  eni_bat: [part("reco", 3, "reco"), ...MANOEUVRE, part("appuis", 0, "mortiers"), part("appuis", 0, "drones", "14")],
  // Face à la brigade : bataillon de reconnaissance, trois bataillons de trois compagnies, bataillon d'artillerie,
  // compagnie de drones.
  eni_brig: [part("reco", 3, "reco"), ...MANOEUVRE, part("appuis", 3, "artillerie"), part("appuis", 0, "drones", "15")],
  // Face à la division : bataillon de reconnaissance, trois brigades de trois bataillons, brigade d'artillerie,
  // bataillon de drones.
  eni_div: [part("reco", 0, "reco", "16"), ...MANOEUVRE, part("appuis", 3, "artillerie"), part("appuis", 0, "drones", "16")],
  // Face au corps d'armée (docs/08 §23) : trois divisions de trois brigades, brigade d'artillerie de corps, bataillon
  // de drones.
  eni_corps: [...MANOEUVRE, part("appuis", 0, "artillerie", "18"), part("appuis", 0, "drones", "16")],
};

/** Fonction des formations de manœuvre d'une structure : celle de la racine si c'est une fonction de mêlée (hors
 *  interarmes), sinon l'infanterie mécanisée. */
export function eniManoeuvreFunction(palette: App6Palette, fonction: string | null, app6: string | null): App6Entry | null {
  const own = entryFor(palette, fonction, app6);
  if (own && own.manoeuvre && own.key !== "interarmes") return own;
  return entryFor(palette, "inf_meca", null);
}

/** Arbre de départ d'un ordre de bataille ennemi, sans faction (ennemi générique : codes « (EN) » donnés par le
 *  serveur) : la racine (échelon par défaut de la nature, fonction, numéros) et, pour la structure ternaire, ses
 *  éléments avec leur rôle — chaque unité nommée « {Échelon} {T} », T enchaînés par échelon, M automatique. */
export function eniStarterTree(ctx: TreeContext, starter: EniStarter): OdbNode {
  const echelon = rootEchelons(ctx)[0] ?? "16";
  const entry = entryFor(ctx.palette, starter.fonction, starter.app6);
  let tree: OdbNode = {
    code: null,
    name: starter.name,
    echelon,
    fonction: starter.fonction,
    app6: starter.app6,
    hq: false,
    tf: null,
    manoeuvre: null,
    number: starter.number,
    higher: starter.higher,
    specialty: entry?.specialty ?? null,
    materiel: [],
    quantity: defaultQuantity(ctx, KIND_ROOT_RANK[ctx.kind]),
    role: null,
    children: [],
  };
  if (starter.start === "racine" || !isEnemyKind(ctx.kind)) return tree;
  const manoeuvre = eniManoeuvreFunction(ctx.palette, starter.fonction, starter.app6);
  const styled = (node: OdbNode, role: EniRole, own: App6Entry | null): OdbNode => ({
    ...node,
    role,
    ...(own ? { fonction: own.key, app6: own.app6, specialty: own.specialty ?? node.specialty ?? null } : {}),
  });
  ENI_TERNARY[ctx.kind as EniOdbKind].forEach((element) => {
    const fn = element.fonction ? entryFor(ctx.palette, element.fonction, null) : manoeuvre;
    const base = newUnit(tree, [], ctx);
    if (!base) return;
    const level = element.echelon ?? base.echelon ?? null;
    const number = nextFreeNumber(tree.children, level);
    const named = level ? { echelon: level, name: echelonUnitName(ctx.palette, level, number), quantity: defaultQuantity(ctx, RANK_ENI[level] ?? null) } : {};
    const unit = styled({ ...base, ...named, number }, element.role, fn);
    tree = { ...tree, children: [...tree.children, unit] };
    if (element.children > 0) {
      const at: Path = [tree.children.length - 1];
      tree = addUnits(tree, at, ctx, element.children);
      tree = updateAt(tree, at, (n) => ({ ...n, children: n.children.map((c) => ({ ...c, role: element.role })) }));
    }
  });
  return tree;
}
